import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import AdminPagination from '../../components/AdminPagination';
import './Reviews.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const Reviews = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [reviews, setReviews] = useState([]);
  const [removingId, setRemovingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState({
    restaurantId: '',
    restaurantType: '',
    deliveryDriverId: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);

  useEffect(() => {
    fetchRestaurants();
    fetchStores();
    fetchDrivers();
    fetchReviews();
  }, [filters]);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const totalPages = Math.max(1, Math.ceil(reviews.length / itemsPerPage));
  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return reviews.slice(start, start + itemsPerPage);
  }, [reviews, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [reviews.length, itemsPerPage]);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      const driversList = response.data.filter(u => u.role === 'delivery_driver');
      setDrivers(driversList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.restaurantId && filters.restaurantType) {
        params.restaurantId = filters.restaurantId;
        params.restaurantType = filters.restaurantType;
      }
      if (filters.deliveryDriverId) {
        params.deliveryDriverId = filters.deliveryDriverId;
      }

      const response = await axios.get(`${API_URL}/admin/reviews`, { params });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showError(t('admin.errorLoadingReviews'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="no-rating">{t('review.noRating')}</span>;
    return (
      <div className="star-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'filled' : ''}>★</span>
        ))}
        <span className="rating-value">({rating})</span>
      </div>
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Reset restaurant filter if type changes
      if (key === 'restaurantType' && value !== prev.restaurantType) {
        newFilters.restaurantId = '';
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      restaurantId: '',
      restaurantType: '',
      deliveryDriverId: ''
    });
  };

  const handleRemoveReview = async (reviewId) => {
    if (!window.confirm(t('reviews.confirmRemove') || 'Remove this review? This cannot be undone.')) return;
    setRemovingId(reviewId);
    try {
      await axios.delete(`${API_URL}/admin/reviews/${reviewId}`);
      showSuccess(t('reviews.removed') || 'Review removed');
      fetchReviews();
    } catch (error) {
      showError(error.response?.data?.message || error.message);
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return <div className="loading-spinner">{t('reviews.loading')}</div>;
  }

  return (
    <div className="reviews-manager">
      <div className="reviews-header">
        <h2>{t('reviews.manager')}</h2>
      </div>

      <div className="reviews-filters">
        <div className="filter-group">
          <label>{t('reviews.filterByRestaurantStore')}:</label>
          <div className="entity-filter">
            <select
              value={filters.restaurantType}
              onChange={(e) => handleFilterChange('restaurantType', e.target.value)}
            >
              <option value="">{t('reviews.allTypes')}</option>
              <option value="Restaurant">{t('admin.restaurant')}</option>
              <option value="Store">{t('admin.store')}</option>
            </select>
            {filters.restaurantType === 'Restaurant' && (
              <select
                value={filters.restaurantId}
                onChange={(e) => handleFilterChange('restaurantId', e.target.value)}
              >
                <option value="">{t('reviews.allRestaurants')}</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant._id} value={restaurant._id}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
            )}
            {filters.restaurantType === 'Store' && (
              <select
                value={filters.restaurantId}
                onChange={(e) => handleFilterChange('restaurantId', e.target.value)}
              >
                <option value="">{t('reviews.allStores')}</option>
                {stores.map(store => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>{t('reviews.filterByDriver')}:</label>
          <select
            value={filters.deliveryDriverId}
            onChange={(e) => handleFilterChange('deliveryDriverId', e.target.value)}
          >
            <option value="">{t('reviews.allDrivers')}</option>
            {drivers.map(driver => (
              <option key={driver._id} value={driver._id}>
                {driver.firstName} {driver.lastName}
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-secondary" onClick={clearFilters}>
          {t('reviews.clearFilters')}
        </button>
      </div>

      <div className="reviews-stats">
        <div className="stat-card">
          <h3>{t('reviews.totalReviews')}</h3>
          <p className="stat-number">{reviews.length}</p>
        </div>
        <div className="stat-card">
          <h3>{t('reviews.avgDeliveryRating')}</h3>
          <p className="stat-number">
            {reviews.filter(r => r.deliveryRating).length > 0
              ? (reviews.reduce((sum, r) => sum + (r.deliveryRating || 0), 0) / reviews.filter(r => r.deliveryRating).length).toFixed(1)
              : 'N/A'}
          </p>
        </div>
        <div className="stat-card">
          <h3>{t('reviews.avgRestaurantRating')}</h3>
          <p className="stat-number">
            {reviews.filter(r => r.restaurantRating).length > 0
              ? (reviews.reduce((sum, r) => sum + (r.restaurantRating || 0), 0) / reviews.filter(r => r.restaurantRating).length).toFixed(1)
              : 'N/A'}
          </p>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="no-reviews">
          <p>{t('reviews.noReviews')}</p>
        </div>
      ) : (
        <div className="reviews-table-container">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>{t('reviews.orderNumber')}</th>
                <th>{t('reviews.customer')}</th>
                <th>{t('reviews.restaurantStore')}</th>
                <th>{t('reviews.deliveryDriver')}</th>
                <th>{t('reviews.deliveryRating')}</th>
                <th>{t('reviews.deliveryComment')}</th>
                <th>{t('reviews.restaurantRating')}</th>
                <th>{t('reviews.restaurantComment')}</th>
                <th>{t('reviews.date')}</th>
                <th>{t('admin.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReviews.map((review) => (
                <tr key={review._id}>
                  <td data-label={t('reviews.orderNumber')}>{review.order?.orderNumber || 'N/A'}</td>
                  <td data-label={t('reviews.customer')}>
                    {review.user?.firstName} {review.user?.lastName}
                    <br />
                    <small>{review.user?.email}</small>
                  </td>
                  <td data-label={t('reviews.restaurantStore')}>{review.order?.restaurantName || 'N/A'}</td>
                  <td data-label={t('reviews.deliveryDriver')}>
                    {review.deliveryDriver
                      ? `${review.deliveryDriver.firstName} ${review.deliveryDriver.lastName}`
                      : 'N/A'}
                  </td>
                  <td data-label={t('reviews.deliveryRating')}>{renderStars(review.deliveryRating)}</td>
                  <td className="comment-cell" data-label={t('reviews.deliveryComment')}>
                    {review.deliveryComment || <span className="no-comment">{t('review.noComment')}</span>}
                  </td>
                  <td data-label={t('reviews.restaurantRating')}>{renderStars(review.restaurantRating)}</td>
                  <td className="comment-cell" data-label={t('reviews.restaurantComment')}>
                    {review.restaurantComment || <span className="no-comment">{t('review.noComment')}</span>}
                  </td>
                  <td data-label={t('reviews.date')}>{formatDate(review.createdAt)}</td>
                  <td data-label={t('admin.actions') || 'Actions'}>
                    <button
                      type="button"
                      className="btn-remove-review"
                      onClick={() => handleRemoveReview(review._id)}
                      disabled={removingId === review._id}
                      title={t('reviews.remove') || 'Remove review'}
                    >
                      {removingId === review._id ? t('admin.loading') || '...' : (t('reviews.remove') || 'Remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reviews.length > 0 && (
        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={reviews.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default Reviews;
